import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { History, RotateCcw, Loader2, ArrowRight, FileDown, Mail, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Estimate version chip + history dialog.
 *
 * Pure presentation: feed it the live `estimateId` and the latest
 * `versionNumber` (rendered in the chip), and the component will fetch
 * the full revision history on demand and offer per-version Restore
 * promoting any prior version to current.
 *
 * Reused across Estimate, Full Report, Planner, Quote, and SOW screens
 * via the shared workspace header — so users see "where am I in the
 * audit trail?" no matter which sub-tab they're on.
 */
export default function EstimateVersionChip({ estimateId, versionNumber, onRestored }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringV, setRestoringV] = useState(null);
  const [deletingV, setDeletingV] = useState(null);
  const [reasonForV, setReasonForV] = useState(null);  // version_number being prompted
  const [restoreReason, setRestoreReason] = useState('');
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  // In-place edit drill-down: maps version_number → {edits: [], loading, error}
  // so each row's amber `N edits` chip can expand inline without
  // re-fetching when toggled twice.
  const [inplaceEdits, setInplaceEdits] = useState({});
  // Audit-trail search: free text over change notes + author filter.
  const [searchText, setSearchText] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const authors = useMemo(() => {
    const s = new Set();
    versions.forEach((v) => {
      if (v.created_by) s.add(v.created_by);
      (v.inplace_edits_brief || []).forEach((e) => { if (e.edited_by) s.add(e.edited_by); });
    });
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [versions]);
  const filterActive = !!(searchText.trim() || authorFilter);
  const filteredVersions = useMemo(() => {
    if (!filterActive) return versions.map((v) => ({ ...v, _matchedEdits: [] }));
    const q = searchText.trim().toLowerCase();
    const textHit = (s) => !q || String(s || '').toLowerCase().includes(q);
    const authorHit = (a) => !authorFilter || (a || 'System') === authorFilter;
    return versions.reduce((acc, v) => {
      const rowHit = textHit(v.change_summary) && authorHit(v.created_by);
      const matchedEdits = (v.inplace_edits_brief || []).filter((e) => textHit(e.change_summary) && authorHit(e.edited_by));
      if (rowHit || matchedEdits.length) acc.push({ ...v, _rowHit: rowHit, _matchedEdits: matchedEdits });
      return acc;
    }, []);
  }, [versions, searchText, authorFilter, filterActive]);
  const highlight = (text) => {
    const q = searchText.trim();
    if (!q || !text) return text;
    const idx = String(text).toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text;
    return (<>{text.slice(0, idx)}<mark className="bg-yellow-200 text-inherit rounded-sm px-0.5">{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>);
  };

  const fetchVersions = useCallback(async () => {
    if (!estimateId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/versions?limit=50`, { withCredentials: true });
      const list = res.data?.versions || [];
      // Sort newest → oldest for display
      list.sort((a, b) => (b.version_number || 0) - (a.version_number || 0));
      setVersions(list);
      // Auto-seed the diff with (prior, current) so the most useful
      // comparison is visible immediately.
      if (list.length >= 2) {
        setCompareA(list[1].version_number);
        setCompareB(list[0].version_number);
      } else {
        setCompareA(null);
        setCompareB(null);
      }
    } catch {
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  // Fetch the diff payload whenever both A and B are set.
  useEffect(() => {
    if (!open || !compareA || !compareB || compareA === compareB) {
      setDiffData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDiffLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/estimates/${estimateId}/versions/diff?v1=${compareA}&v2=${compareB}`,
          { withCredentials: true },
        );
        if (!cancelled) setDiffData(res.data || null);
      } catch {
        if (!cancelled) setDiffData(null);
      } finally {
        if (!cancelled) setDiffLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, compareA, compareB, estimateId]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setSearchText('');
    setAuthorFilter('');
    await fetchVersions();
  }, [fetchVersions]);

  const handleRestore = useCallback(async (vn, reason = '') => {
    if (!estimateId || !vn) return;
    setRestoringV(vn);
    try {
      const res = await axios.post(
        `${API_URL}/api/estimates/${estimateId}/versions/${vn}/restore`,
        { reason: (reason || '').trim() || null },
        { withCredentials: true },
      );
      toast.success(`Promoted v${vn} to current · saved as v${res.data?.restored_version || '?'}`);
      await fetchVersions();
      // Notify parent so the page can reload its data with the freshly
      // restored snapshot. Falls back to a hard reload if no callback.
      if (typeof onRestored === 'function') {
        onRestored();
      } else {
        window.location.reload();
      }
    } catch {
      toast.error(`Failed to promote v${vn}`);
    } finally {
      setRestoringV(null);
      setReasonForV(null);
      setRestoreReason('');
    }
  }, [estimateId, fetchVersions, onRestored]);

  // Open the reason-prompt dialog for a specific version. The actual
  // restore call fires only on confirm, so users can cancel without side
  // effects.
  const promptReasonAndRestore = useCallback((vn) => {
    setRestoreReason('');
    setReasonForV(vn);
  }, []);

  // Super-admin only — permanently delete a single historical version
  // row. Refuses on the only remaining version (server-side check)
  // and confirms before firing because it's irreversible.
  const deleteVersion = useCallback(async (vn) => {
    if (!estimateId) return;
    if (!window.confirm(`Permanently delete v${vn}?\n\nThis is irreversible. Other versions and the live estimate are unaffected.`)) return;
    setDeletingV(vn);
    try {
      await axios.delete(
        `${API_URL}/api/estimates/${estimateId}/versions/${vn}`,
        { withCredentials: true },
      );
      toast.success(`v${vn} deleted`);
      // Refresh the dialog list so the row disappears and counts update.
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/versions`, { withCredentials: true });
      setVersions(res.data.versions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete version');
    } finally {
      setDeletingV(null);
    }
  }, [estimateId]);

  // ── Audit-log PDF export ────────────────────────────────────────
  const exportAuditPdf = useCallback(async () => {
    if (!estimateId) return;
    setExportingPdf(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/audit-log.pdf`,
        { withCredentials: true, responseType: 'blob' },
      );
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `estimate_audit_log_${new Date().toISOString().slice(0,10)}.pdf`;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Audit log PDF downloaded');
    } catch {
      toast.error('Failed to export audit log PDF');
    } finally {
      setExportingPdf(false);
    }
  }, [estimateId]);

  // ── Email the audit log PDF to compliance ──────────────────────
  // Reuses the existing /share/email pattern (Resend-backed) so the PM
  // can mail the PDF straight to a compliance owner without leaving
  // the dialog.
  const sendAuditEmail = useCallback(async () => {
    const to = (emailRecipient || '').trim();
    if (!estimateId || !to) {
      toast.error('Enter a recipient email');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/audit-log/email`,
        { recipient: to },
        { withCredentials: true },
      );
      toast.success(`Audit log emailed to ${to}`);
      setEmailOpen(false);
      setEmailRecipient('');
    } catch {
      toast.error('Failed to email audit log');
    } finally {
      setSendingEmail(false);
    }
  }, [estimateId, emailRecipient]);

  // Toggle the in-place edits drill-down for a given version. Lazy-loads
  // the full version detail (which carries `inplace_edits[]`) the first
  // time it's expanded; subsequent toggles flip the open flag without a
  // refetch.
  const toggleInplaceEdits = useCallback(async (vn) => {
    setInplaceEdits((prev) => {
      const cur = prev[vn];
      if (cur?.loaded) {
        return { ...prev, [vn]: { ...cur, expanded: !cur.expanded } };
      }
      return { ...prev, [vn]: { expanded: true, loading: true, edits: [], loaded: false } };
    });
    const cached = inplaceEdits[vn];
    if (cached?.loaded) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/versions/${vn}`,
        { withCredentials: true },
      );
      const edits = res.data?.inplace_edits || [];
      setInplaceEdits((prev) => ({
        ...prev,
        [vn]: { expanded: true, loading: false, edits, loaded: true },
      }));
    } catch {
      setInplaceEdits((prev) => ({
        ...prev,
        [vn]: { expanded: true, loading: false, edits: [], loaded: true, error: true },
      }));
    }
  }, [estimateId, inplaceEdits]);

  const fmtMoney = (n, ccy) => {
    if (n == null) return '—';
    const sym = ccy && ccy !== 'USD' ? '' : '$';
    return sym + Math.round(Number(n)).toLocaleString() + (ccy && ccy !== 'USD' ? ` ${ccy}` : '');
  };
  const fmtHours = (n) => n == null ? '—' : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`;
  const fmtWeeks = (n) => n == null ? '—' : `${Math.round(Number(n))}w`;
  const fmtDelta = (n, prefix = '') => {
    if (n == null || Math.abs(n) < 0.01) return null;
    const sign = n > 0 ? '+' : '';
    return `${sign}${prefix}${prefix === '$' ? Math.round(Math.abs(n)).toLocaleString() : Math.round(n).toLocaleString()}`;
  };

  const v = versionNumber || 1;

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-200 px-2 py-0.5 rounded transition-colors"
        title="View version history"
        data-testid="estimate-version-chip"
      >
        <History className="w-3 h-3" />
        v{v}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="estimate-version-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <History className="w-4 h-4" /> Estimate Version History
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Every save creates a versioned snapshot. Click <strong>A</strong>/<strong>B</strong> on any two rows to see what changed. Hit <strong>Promote to current</strong> on a prior version to roll the live estimate back — the current state is preserved as a new revision so the action is itself reversible.
            </DialogDescription>
          </DialogHeader>

          {/* Diff panel — 3-up rollup (Cost · Effort · Timeline) plus a
              per-screen line-item highlight section that calls out which
              components actually moved between the two versions. */}
          {diffData && diffData.v1 != null && diffData.v2 != null && (
            <div className="border border-slate-200 rounded-md p-3 bg-gradient-to-br from-slate-50 to-white space-y-2.5" data-testid="version-diff-panel">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Compare</span>
                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">v{diffData.v1}</span>
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">v{diffData.v2}</span>
                {diffLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-1" />}
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white border border-slate-200 rounded p-2" data-testid="diff-cost">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Total Cost</p>
                  <p className="text-[11px] text-slate-500">
                    <span className="font-mono">{fmtMoney(diffData.cost?.old)}</span>
                    <span className="text-slate-300"> → </span>
                    <span className="font-mono font-semibold text-slate-700">{fmtMoney(diffData.cost?.new)}</span>
                  </p>
                  {diffData.cost?.delta != null && Math.abs(diffData.cost.delta) >= 1 && (
                    <p className={`text-[11px] font-semibold mt-0.5 ${diffData.cost.delta > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {fmtDelta(diffData.cost.delta, '$')}
                    </p>
                  )}
                </div>
                <div className="bg-white border border-slate-200 rounded p-2" data-testid="diff-effort">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Total Effort</p>
                  <p className="text-[11px] text-slate-500">
                    <span className="font-mono">{fmtHours(diffData.effort?.old)}</span>
                    <span className="text-slate-300"> → </span>
                    <span className="font-mono font-semibold text-slate-700">{fmtHours(diffData.effort?.new)}</span>
                  </p>
                  {diffData.effort?.delta != null && Math.abs(diffData.effort.delta) >= 0.5 && (
                    <p className={`text-[11px] font-semibold mt-0.5 ${diffData.effort.delta > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {fmtDelta(diffData.effort.delta)} hrs
                    </p>
                  )}
                </div>
                <div className="bg-white border border-slate-200 rounded p-2" data-testid="diff-timeline">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Timeline</p>
                  <p className="text-[11px] text-slate-500">
                    <span className="font-mono">{fmtWeeks(diffData.timeline?.old)}</span>
                    <span className="text-slate-300"> → </span>
                    <span className="font-mono font-semibold text-slate-700">{fmtWeeks(diffData.timeline?.new)}</span>
                  </p>
                  {diffData.timeline?.delta != null && Math.abs(diffData.timeline.delta) >= 0.5 && (
                    <p className={`text-[11px] font-semibold mt-0.5 ${diffData.timeline.delta > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {fmtDelta(diffData.timeline.delta)}w
                    </p>
                  )}
                </div>
              </div>

              {/* Per-line-item highlights — what actually moved. Caps at
                  6 rows per bucket so the panel stays compact; the rest
                  collapses behind a "+N more" label. */}
              {(() => {
                const added = diffData.components?.added || [];
                const removed = diffData.components?.removed || [];
                const changed = (diffData.components?.changed || []).filter(c =>
                  (c.effort_delta != null && Math.abs(c.effort_delta) >= 0.5) ||
                  (c.cost_delta != null && Math.abs(c.cost_delta) >= 1) ||
                  Object.keys(c.changes || {}).length > 0
                );
                const cfgFields = Object.keys(diffData.config_changes || {});
                if (added.length === 0 && removed.length === 0 && changed.length === 0 && cfgFields.length === 0) {
                  return (
                    <p className="text-[10px] text-slate-400 italic">No component-level or config changes between these versions.</p>
                  );
                }
                const cap = 6;
                const renderList = (items, color, signLabel, render) => items.length > 0 && (
                  <div className="space-y-0.5">
                    <p className={`text-[10px] font-semibold ${color} uppercase tracking-wide`}>{signLabel} ({items.length})</p>
                    {items.slice(0, cap).map((it, idx) => render(it, idx))}
                    {items.length > cap && <p className="text-[10px] text-slate-400 italic">…and {items.length - cap} more</p>}
                  </div>
                );
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                    {renderList(added, 'text-emerald-700', 'Added', (it, idx) => (
                      <p key={`a-${idx}`} className="text-[11px] text-slate-600 truncate" title={it.component_name}>
                        <span className="font-medium">+ {it.component_name}</span>
                        {it.metrics?.effort != null && <span className="text-slate-400 ml-1">· {fmtHours(it.metrics.effort)}</span>}
                        {it.metrics?.cost != null && <span className="text-slate-400 ml-1">· {fmtMoney(it.metrics.cost)}</span>}
                      </p>
                    ))}
                    {renderList(removed, 'text-red-700', 'Removed', (it, idx) => (
                      <p key={`r-${idx}`} className="text-[11px] text-slate-600 truncate" title={it.component_name}>
                        <span className="font-medium">- {it.component_name}</span>
                        {it.metrics?.effort != null && <span className="text-slate-400 ml-1">· {fmtHours(it.metrics.effort)}</span>}
                      </p>
                    ))}
                    {renderList(changed, 'text-amber-700', 'Modified', (it, idx) => (
                      <p key={`c-${idx}`} className="text-[11px] text-slate-600 truncate" title={it.component_name}>
                        <span className="font-medium">~ {it.component_name}</span>
                        {it.effort_delta != null && Math.abs(it.effort_delta) >= 0.5 && (
                          <span className={`ml-1 font-semibold ${it.effort_delta > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{fmtDelta(it.effort_delta)} hrs</span>
                        )}
                        {it.cost_delta != null && Math.abs(it.cost_delta) >= 1 && (
                          <span className={`ml-1 ${it.cost_delta > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>· {fmtDelta(it.cost_delta, '$')}</span>
                        )}
                      </p>
                    ))}
                    {cfgFields.length > 0 && (
                      <div className="space-y-0.5 md:col-span-2">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Config Changes ({cfgFields.length})</p>
                        {cfgFields.slice(0, cap).map((k) => (
                          <p key={k} className="text-[11px] text-slate-600 truncate" title={`${k}: ${diffData.config_changes[k].old} → ${diffData.config_changes[k].new}`}>
                            <span className="text-slate-400">{k}:</span> {String(diffData.config_changes[k].old || '—')} <span className="text-slate-300">→</span> <span className="font-medium">{String(diffData.config_changes[k].new || '—')}</span>
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {versions.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center" data-testid="version-history-filter-bar">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search change notes…"
                  className="h-8 pl-8 text-xs"
                  data-testid="version-history-search-input"
                />
              </div>
              <select
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
                className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 min-w-[160px]"
                title="Filter by author"
                data-testid="version-history-author-select"
              >
                <option value="">All authors ({authors.length})</option>
                {authors.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              {filterActive && (
                <button
                  type="button"
                  onClick={() => { setSearchText(''); setAuthorFilter(''); }}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#0F2040] whitespace-nowrap"
                  data-testid="version-history-clear-filters"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
              {filterActive && (
                <span className="text-[11px] text-slate-500 whitespace-nowrap" data-testid="version-history-match-count">
                  {filteredVersions.length} of {versions.length} version{versions.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
          )}

          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
            {loading && (
              <div className="flex items-center justify-center py-8 text-slate-400 text-xs">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading versions…
              </div>
            )}
            {!loading && versions.length === 0 && (
              <p className="text-center text-slate-400 text-xs py-8">No versions on file yet.</p>
            )}
            {!loading && versions.length > 0 && filteredVersions.length === 0 && (
              <p className="text-center text-slate-400 text-xs py-8" data-testid="version-history-no-matches">No versions or edits match your search.</p>
            )}
            {filteredVersions.map((ver) => {
              const isCurrent = ver.version_number === versions[0]?.version_number;
              const isA = compareA === ver.version_number;
              const isB = compareB === ver.version_number;              return (
                <div
                  key={ver.version_number}
                  className={`border rounded-md p-3 ${isCurrent ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-white'}`}
                  data-testid={`estimate-version-row-${ver.version_number}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-bold text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded">v{ver.version_number}</span>
                        {isCurrent && <span className="text-[9px] font-semibold text-blue-700">CURRENT</span>}
                        {ver.summary?.total_effort != null && (
                          <span className="text-[10px] font-mono text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5" title="Total effort at this version">
                            {fmtHours(ver.summary.total_effort)}
                          </span>
                        )}
                        {ver.summary?.total_cost != null && (
                          <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" title="Total cost at this version">
                            {fmtMoney(ver.summary.total_cost, ver.summary.currency_code)}
                          </span>
                        )}
                        {ver.summary?.projected_weeks != null && (
                          <span
                            className="text-[10px] font-mono text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"
                            title={`Calculated timeline at this version${ver.summary?.committed_weeks ? ` · planner committed: ${ver.summary.committed_weeks}w` : ''}`}
                          >
                            {fmtWeeks(ver.summary.projected_weeks)}
                            {ver.summary.committed_weeks != null && ver.summary.committed_weeks !== ver.summary.projected_weeks && (
                              <span className="text-slate-400 ml-0.5">→ {fmtWeeks(ver.summary.committed_weeks)}</span>
                            )}
                          </span>
                        )}
                        {ver.inplace_edit_count > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleInplaceEdits(ver.version_number)}
                            className={`text-[10px] font-mono rounded px-1.5 py-0.5 border transition-colors cursor-pointer ${
                              inplaceEdits[ver.version_number]?.expanded
                                ? 'text-amber-800 bg-amber-100 border-amber-300'
                                : 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
                            }`}
                            title={`Click to ${inplaceEdits[ver.version_number]?.expanded ? 'collapse' : 'expand'} the in-place edit timeline · last ${ver.last_inplace_edit_at ? new Date(ver.last_inplace_edit_at).toLocaleString() : '—'}`}
                            data-testid={`version-inplace-count-${ver.version_number}`}
                          >
                            {ver.inplace_edit_count} edit{ver.inplace_edit_count === 1 ? '' : 's'}
                            <span className="ml-1 text-[8px]">{inplaceEdits[ver.version_number]?.expanded ? '▾' : '▸'}</span>
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">
                        <span className="font-medium text-[#0F2040]">{ver.created_by || 'System'}</span>
                        <span className="text-slate-400"> · {ver.created_at ? new Date(ver.created_at).toLocaleString() : '—'}</span>
                      </p>
                      {ver.change_summary && (
                        <p className="text-xs text-slate-500 italic mt-1 line-clamp-2" title={ver.change_summary}>"{highlight(ver.change_summary)}"</p>
                      )}
                      {filterActive && ver._matchedEdits.length > 0 && (
                        <div className="mt-2 pl-2 border-l-2 border-yellow-300 space-y-1" data-testid={`version-matched-edits-${ver.version_number}`}>
                          <p className="text-[10px] font-semibold text-yellow-700 uppercase tracking-wide">{ver._matchedEdits.length} matching in-place edit{ver._matchedEdits.length === 1 ? '' : 's'}</p>
                          {ver._matchedEdits.slice().reverse().map((ed, i) => (
                            <div key={i} className="text-[11px] leading-snug" data-testid={`version-matched-edit-${ver.version_number}-${i}`}>
                              <span className="font-medium text-[#0F2040]">{ed.edited_by || 'System'}</span>
                              <span className="text-slate-400 text-[10px]"> · {ed.edited_at ? new Date(ed.edited_at).toLocaleString() : ''}</span>
                              {ed.change_summary && <p className="text-slate-500 italic line-clamp-2" title={ed.change_summary}>"{highlight(ed.change_summary)}"</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => setCompareA(isA ? null : ver.version_number)}
                          className={`text-[9px] font-bold rounded px-1.5 py-0.5 border transition-colors ${
                            isA ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-500 border-slate-300 hover:border-[#0F2040] hover:text-[#0F2040]'
                          }`}
                          title="Set as compare A (before)"
                          data-testid={`compare-a-${ver.version_number}`}
                        >A</button>
                        <button
                          type="button"
                          onClick={() => setCompareB(isB ? null : ver.version_number)}
                          className={`text-[9px] font-bold rounded px-1.5 py-0.5 border transition-colors ${
                            isB ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]' : 'bg-white text-slate-500 border-slate-300 hover:border-[#4ABFCF] hover:text-[#4ABFCF]'
                          }`}
                          title="Set as compare B (after)"
                          data-testid={`compare-b-${ver.version_number}`}
                        >B</button>
                      </div>
                      {!isCurrent && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => promptReasonAndRestore(ver.version_number)}
                          disabled={restoringV === ver.version_number}
                          data-testid={`promote-version-${ver.version_number}`}
                        >
                          {restoringV === ver.version_number ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <RotateCcw className="w-3 h-3 mr-1" />
                          )}
                          Promote to current
                        </Button>
                      )}
                      {isSuperAdmin && versions.length > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs text-red-600 hover:text-red-700 hover:border-red-300"
                          onClick={() => deleteVersion(ver.version_number)}
                          disabled={deletingV === ver.version_number}
                          data-testid={`delete-version-${ver.version_number}`}
                          title="Super-admin: permanently delete this version row"
                        >
                          {deletingV === ver.version_number ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* In-place edits drill-down — surfaced when the user
                      clicks the amber `N edits` chip. Renders a compact
                      vertical timeline of every mid-session save folded
                      into this version. Useful for compliance reviewers
                      who want to see *every* change in a session
                      without bumping the version number. */}
                  {inplaceEdits[ver.version_number]?.expanded && (
                    <div className="mt-3 pl-3 border-l-2 border-amber-200 space-y-1.5" data-testid={`version-inplace-timeline-${ver.version_number}`}>
                      <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1">
                        <span>In-place edits</span>
                        {inplaceEdits[ver.version_number].loading && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                      </p>
                      {!inplaceEdits[ver.version_number].loading && inplaceEdits[ver.version_number].edits.length === 0 && (
                        <p className="text-[10px] text-slate-400 italic">No edit details on file.</p>
                      )}
                      {(inplaceEdits[ver.version_number].edits || []).slice().reverse().map((ed, i) => (
                        <div key={i} className="text-[11px] leading-snug" data-testid={`inplace-edit-${ver.version_number}-${i}`}>
                          <div className="flex items-baseline gap-2">
                            <span className="text-[9px] font-mono text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0 shrink-0">
                              #{(inplaceEdits[ver.version_number].edits.length - i)}
                            </span>
                            <span className="font-medium text-[#0F2040] truncate">{ed.edited_by || 'System'}</span>
                            <span className="text-slate-400 text-[10px] shrink-0">{ed.edited_at ? new Date(ed.edited_at).toLocaleString() : ''}</span>
                          </div>
                          {ed.change_summary && (
                            <p className="text-[11px] text-slate-500 italic mt-0.5 line-clamp-2 ml-7" title={ed.change_summary}>"{ed.change_summary}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Compliance footer — audit-log PDF export + email-to-compliance.
              Lives inside the dialog so audit packs are one click away
              from the History view. */}
          {versions.length > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <p className="text-[10px] text-slate-400">{versions.length} version{versions.length === 1 ? '' : 's'} on file · audit-grade chronological log</p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => setEmailOpen(true)}
                  data-testid="audit-email-btn"
                >
                  <Mail className="w-3 h-3 mr-1.5" /> Email to Compliance
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={exportAuditPdf}
                  disabled={exportingPdf}
                  data-testid="audit-pdf-btn"
                >
                  {exportingPdf ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <FileDown className="w-3 h-3 mr-1.5" />}
                  Export PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email-to-compliance dialog. Recipient is the only required field;
          message body is hard-coded server-side to keep audit emails
          consistent and tamper-resistant. */}
      <Dialog open={emailOpen} onOpenChange={(o) => { if (!o) { setEmailOpen(false); setEmailRecipient(''); } }}>
        <DialogContent className="max-w-md" data-testid="audit-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <Mail className="w-4 h-4" /> Email Audit Log
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Sends the same PDF as Export, with a short signed email body identifying the estimate and the requesting user. Recipients should be your organisation's compliance owner or PMO archivist.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-slate-500" htmlFor="audit-email-input">Recipient email</label>
            <input
              id="audit-email-input"
              type="email"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              placeholder="compliance@example.com"
              className="w-full text-sm border border-slate-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
              data-testid="audit-email-input"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => { setEmailOpen(false); setEmailRecipient(''); }} data-testid="audit-email-cancel">Cancel</Button>
            <Button
              size="sm"
              className="text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white"
              onClick={sendAuditEmail}
              disabled={sendingEmail || !emailRecipient.trim()}
              data-testid="audit-email-send"
            >
              {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Mail className="w-3 h-3 mr-1.5" />}
              Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reason-prompt dialog — captures the PM's "why" before issuing the
          actual restore. Confirm fires the API call; Cancel is a no-op. */}
      <Dialog open={!!reasonForV} onOpenChange={(o) => { if (!o) { setReasonForV(null); setRestoreReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="restore-reason-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <RotateCcw className="w-4 h-4" /> Promote v{reasonForV} to current
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              The current state will be auto-saved as a new revision before this rollback so the action remains reversible. Tell us why — this gets stamped on the audit row so future reviews know <strong className="text-slate-500">why</strong> we rolled back, not just <strong className="text-slate-500">what</strong> changed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-slate-500" htmlFor="restore-reason-input">
              Reason <span className="text-slate-300 font-normal">(optional, &lt;= 200 chars)</span>
            </label>
            <textarea
              id="restore-reason-input"
              value={restoreReason}
              onChange={(e) => setRestoreReason(e.target.value.slice(0, 200))}
              placeholder="e.g. rolling back vendor pricing change · client requested phase 2 split"
              rows={3}
              className="w-full text-sm border border-slate-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
              data-testid="restore-reason-input"
              autoFocus
            />
            <p className="text-[10px] text-slate-400 text-right">{restoreReason.length}/200</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => { setReasonForV(null); setRestoreReason(''); }}
              data-testid="restore-reason-cancel"
            >Cancel</Button>
            <Button
              size="sm"
              className="text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white"
              onClick={() => handleRestore(reasonForV, restoreReason)}
              disabled={restoringV === reasonForV}
              data-testid="restore-reason-confirm"
            >
              {restoringV === reasonForV ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <RotateCcw className="w-3 h-3 mr-1" />
              )}
              Promote to current
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
